import React, { useState, useEffect } from 'react';
import { generateClient } from 'aws-amplify/data';
import { logError } from '../../utils/logger';
import {
  ApprovedCatch, CompetitionEvent, filterCatchesByYear,
  computeSeasonSummary, computeSpeciesBreakdown, computeCompetitorLeaderboard,
  findBiggestFish, computeCompetitionTrends, generateCsvContent,
} from '../../utils/seasonStatsUtils';
import { speciesLabel } from '../../utils/memberStats';
import { computeSeasonStandings } from '../../utils/championPoints';
import { formatClubDate } from '../../utils/clubTime';

const client = generateClient({ authMode: 'userPool' });

const SeasonStatsDashboard: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [years, setYears] = useState<number[]>([currentYear]);
  const [catches, setCatches] = useState<ApprovedCatch[]>([]);
  const [events, setEvents] = useState<CompetitionEvent[]>([]);
  const [allRawEvents, setAllRawEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch all competition events
      let allEvents: any[] = [];
      let nextToken: string | null = null;
      do {
        const result: any = await client.models.Event.list({
          filter: { eventType: { eq: 'COMPETITION' } },
          ...(nextToken ? { nextToken } : {}),
        });
        allEvents = allEvents.concat(result.data || []);
        nextToken = result.nextToken ?? null;
      } while (nextToken);

      const mappedEvents: CompetitionEvent[] = allEvents.map((e: any) => ({ id: e.id, title: e.title, eventDate: e.eventDate }));
      setEvents(mappedEvents);
      setAllRawEvents(allEvents);

      // Populate year selector
      const eventYears = Array.from(new Set(mappedEvents.map((e: CompetitionEvent) => new Date(e.eventDate).getFullYear()))).sort((a: number, b: number) => b - a);
      if (eventYears.length > 0) setYears(eventYears);

      // Fetch all catches for all events
      let allCatches: any[] = [];
      for (const evt of allEvents) {
        let evtToken: string | null = null;
        do {
          const result: any = await client.models.CatchSubmission.list({
            filter: { eventId: { eq: evt.id } },
            ...(evtToken ? { nextToken: evtToken } : {}),
          });
          allCatches = allCatches.concat(result.data || []);
          evtToken = result.nextToken ?? null;
        } while (evtToken);
      }

      const approved: ApprovedCatch[] = allCatches
        .filter((c: any) => c.status === 'APPROVED')
        .map((c: any) => ({
          id: c.id, eventId: c.eventId, species: c.species, lengthCm: c.lengthCm,
          competitorName: c.competitorName, keptOrReleased: c.keptOrReleased, submissionTimestamp: c.submissionTimestamp,
        }));
      setCatches(approved);
    } catch (err: any) {
      logError('SeasonStatsDashboard', err);
      setError('Failed to load season statistics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const filtered = filterCatchesByYear(catches, events, year);
  const summary = computeSeasonSummary(filtered, events);
  const speciesBreakdown = computeSpeciesBreakdown(filtered);
  const leaderboard = computeCompetitorLeaderboard(filtered);
  const biggest = findBiggestFish(filtered);
  const trends = computeCompetitionTrends(filtered, events);

  const handleExportCSV = () => {
    const csv = generateCsvContent(filtered, events);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tfc-season-stats-${year}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  if (loading) return <div className="text-center py-12 text-gray-500">Loading season statistics…</div>;
  if (error) return (
    <div className="bg-red-50 border border-red-200 rounded-md p-4">
      <p className="text-red-800">{error}</p>
      <button onClick={fetchData} className="mt-2 text-sm text-red-600 hover:text-red-800 font-medium">Retry</button>
    </div>
  );

  return (
    <div>
      {/* Header with year selector and export */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-bold text-gray-900">Season Statistics</h2>
          <select value={year} onChange={(e: any) => setYear(Number(e.target.value))} className="px-3 py-2 border border-gray-300 rounded-md text-sm" aria-label="Select year">
            {years.map((y: number) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <button onClick={handleExportCSV} disabled={filtered.length === 0} className="px-4 py-2 text-sm font-medium text-white bg-fishing-blue hover:bg-blue-700 rounded-md disabled:bg-gray-300">
          Export CSV
        </button>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-12"><div className="text-5xl mb-3">🎣</div><p className="text-gray-600">No approved catches recorded for {year}</p></div>
      ) : (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            <div className="bg-white shadow rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-gray-900">{summary.totalCatches}</div>
              <div className="text-xs text-gray-500">Total Catches</div>
            </div>
            <div className="bg-white shadow rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-green-600">{summary.keptCount} / {summary.releasedCount}</div>
              <div className="text-xs text-gray-500">Kept ({summary.keptPercent}%) / Released ({summary.releasedPercent}%)</div>
            </div>
            <div className="bg-white shadow rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-fishing-blue">{summary.uniqueCompetitors}</div>
              <div className="text-xs text-gray-500">Unique Competitors</div>
            </div>
            <div className="bg-white shadow rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-purple-600">{summary.competitionsHeld}</div>
              <div className="text-xs text-gray-500">Competitions Held</div>
            </div>
          </div>

          {/* Biggest fish */}
          {biggest && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 flex items-center gap-3">
              <span className="text-3xl">🏆</span>
              <div>
                <div className="font-semibold text-gray-900">Biggest Fish of {year}</div>
                <div className="text-sm text-gray-700">{speciesLabel(biggest.species)} — {biggest.lengthCm}cm by {biggest.competitorName}</div>
              </div>
            </div>
          )}

          {/* Species breakdown */}
          <div className="bg-white shadow rounded-lg overflow-hidden mb-6">
            <h3 className="px-4 py-3 font-semibold text-gray-900 border-b">Species Breakdown</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Species</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Count</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Avg Length</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Biggest</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Record Holder</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {speciesBreakdown.map((s: any) => (
                    <tr key={s.species}>
                      <td className="px-4 py-2 text-sm font-medium text-gray-900">{speciesLabel(s.species)}</td>
                      <td className="px-4 py-2 text-sm text-gray-700 text-right">{s.count}</td>
                      <td className="px-4 py-2 text-sm text-gray-700 text-right">{s.avgLength}cm</td>
                      <td className="px-4 py-2 text-sm text-gray-700 text-right">{s.maxLength}cm</td>
                      <td className="px-4 py-2 text-sm text-gray-700">{s.maxLengthHolder}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Competitor leaderboard */}
          <div className="bg-white shadow rounded-lg overflow-hidden mb-6">
            <h3 className="px-4 py-3 font-semibold text-gray-900 border-b">Competitor Leaderboard</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Competitor</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Catches</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Biggest</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Comps</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {leaderboard.map((c: any, i: number) => (
                    <tr key={c.name} className={i < 3 ? 'bg-yellow-50' : ''}>
                      <td className="px-4 py-2 text-sm font-medium text-gray-900">{i + 1}</td>
                      <td className="px-4 py-2 text-sm text-gray-900">{c.name}</td>
                      <td className="px-4 py-2 text-sm text-gray-700 text-right">{c.totalCatches}</td>
                      <td className="px-4 py-2 text-sm text-gray-700 text-right">{c.biggestFish}cm</td>
                      <td className="px-4 py-2 text-sm text-gray-700 text-right">{c.competitionsParticipated}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Competition trends */}
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <h3 className="px-4 py-3 font-semibold text-gray-900 border-b">Competition Trends</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Competition</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Catches</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Avg Length</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Competitors</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {trends.map((t: any) => (
                    <tr key={t.eventId}>
                      <td className="px-4 py-2 text-sm text-gray-900">{t.eventTitle}</td>
                      <td className="px-4 py-2 text-sm text-gray-700">{t.eventDate ? formatClubDate(t.eventDate) : ''}</td>
                      <td className="px-4 py-2 text-sm text-gray-700 text-right">{t.totalCatches}</td>
                      <td className="px-4 py-2 text-sm text-gray-700 text-right">{t.avgLength}cm</td>
                      <td className="px-4 py-2 text-sm text-gray-700 text-right">{t.uniqueCompetitors}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Champion Points Standings */}
          {(() => {
            const champStandings = computeSeasonStandings(allRawEvents, year);
            const hasSenior = champStandings.seniorStandings.length > 0;
            const hasJunior = champStandings.juniorStandings.length > 0;
            return (
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <h3 className="px-4 py-3 font-semibold text-gray-900 border-b">🏆 Champion Points</h3>
                {!hasSenior && !hasJunior ? (
                  <p className="px-4 py-4 text-sm text-gray-500 italic">No champion data for {year}</p>
                ) : (
                  <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Senior Division</h4>
                      {hasSenior ? (
                        <table className="w-full text-sm">
                          <thead><tr className="border-b text-left text-gray-500"><th className="py-1">Name</th><th className="py-1 text-right">Pts</th><th className="py-1 text-right">Comps</th></tr></thead>
                          <tbody>
                            {champStandings.seniorStandings.map((s) => (
                              <tr key={s.memberSub} className="border-b last:border-b-0"><td className="py-1">{s.competitorName}</td><td className="py-1 text-right font-medium">{s.totalPoints}</td><td className="py-1 text-right text-gray-600">{s.competitionsCount}</td></tr>
                            ))}
                          </tbody>
                        </table>
                      ) : <p className="text-sm text-gray-500 italic">No data</p>}
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Junior Division</h4>
                      {hasJunior ? (
                        <table className="w-full text-sm">
                          <thead><tr className="border-b text-left text-gray-500"><th className="py-1">Name</th><th className="py-1 text-right">Pts</th><th className="py-1 text-right">Comps</th></tr></thead>
                          <tbody>
                            {champStandings.juniorStandings.map((s) => (
                              <tr key={s.memberSub} className="border-b last:border-b-0"><td className="py-1">{s.competitorName}</td><td className="py-1 text-right font-medium">{s.totalPoints}</td><td className="py-1 text-right text-gray-600">{s.competitionsCount}</td></tr>
                            ))}
                          </tbody>
                        </table>
                      ) : <p className="text-sm text-gray-500 italic">No data</p>}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </>
      )}
    </div>
  );
};

export default SeasonStatsDashboard;
